import {
  ModelFamily,
  ModelRouteAdapter,
  ModelSurface,
  ModelTopology,
  ReferenceTransportStrategy,
  mergeModelContracts,
} from '../../config/models';
import {
  getCurrentModelById,
  getCurrentModelIdSet,
  getCurrentModelRepository,
  getCurrentTimingProfileForKey,
} from './modelRepositoryRuntime';
import { getSceneAssetReferenceBucketKind } from '../../types/scene-assets/sceneAssetKinds';

export { ModelFamily, ModelRouteAdapter, ModelSurface, ModelTopology };

export const MODEL_FAMILY_ORDER = [
  ModelFamily.IMAGE,
  ModelFamily.VIDEO,
  ModelFamily.THREE_D,
  ModelFamily.AUDIO,
];

export const MODEL_FAMILY_LABELS = {
  [ModelFamily.IMAGE]: 'Images',
  [ModelFamily.VIDEO]: 'Video',
  [ModelFamily.THREE_D]: '3D',
  [ModelFamily.AUDIO]: 'Audio',
};
function asArray(value) {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
}

function uniqueStrings(values = []) {
  const seen = new Set();
  const result = [];

  for (const value of Array.isArray(values) ? values : []) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }

  return result;
}

function resolveModel(modelOrId) {
  if (!modelOrId) return null;
  if (typeof modelOrId === 'object') return modelOrId;
  return getModelById(modelOrId);
}

function includesAll(candidateValues = [], requiredValues = []) {
  const required = asArray(requiredValues).filter(Boolean);
  if (required.length === 0) return true;
  const candidateSet = new Set(candidateValues);
  return required.every((value) => candidateSet.has(value));
}

function matchesSearch(model, search = '') {
  const query = String(search || '').trim().toLowerCase();
  if (!query) return true;

  const haystack = [
    model.id,
    model.label,
    model.description,
    model.presentation?.label,
    model.presentation?.shortLabel,
    model.presentation?.description,
    model.family,
    model.provider,
    ...(model.tags || []),
    ...(model.presentation?.tags || []),
    ...(model.topologies || []),
    ...(model.surfaces || []),
  ].filter(Boolean).join(' ').toLowerCase();

  return haystack.includes(query);
}

function isFeatureEnabled(value) {
  if (value == null || value === false) return false;
  if (value === true) return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return value.enabled !== false;
  return Boolean(value);
}

function mergeFeatureContracts(base = {}, override = {}) {
  const merged = { ...(base || {}), ...(override || {}) };

  for (const key of Object.keys(merged)) {
    if (
      base?.[key]
      && override?.[key]
      && typeof base[key] === 'object'
      && typeof override[key] === 'object'
      && !Array.isArray(base[key])
      && !Array.isArray(override[key])
    ) {
      merged[key] = { ...base[key], ...override[key] };
    }
  }

  return merged;
}

export const getAllModels = () => getCurrentModelRepository();

export function getModelById(modelId) {
  if (!modelId || typeof modelId !== 'string') return null;
  return getCurrentModelById(modelId);
}

export const hasModel = (modelId) => getCurrentModelIdSet().has(modelId);

export function getDefaultModelActivationState(models = getCurrentModelRepository()) {
  return Object.fromEntries(
    models.map((model) => [model.id, model.activation?.defaultActive !== false]),
  );
}

export function normalizeModelActivationOverrides(overrides = {}) {
  const input = overrides && typeof overrides === 'object' ? overrides : {};
  const normalized = {};
  const idSet = getCurrentModelIdSet();

  for (const [modelId, enabled] of Object.entries(input)) {
    if (!idSet.has(modelId) || typeof enabled !== 'boolean') continue;

    const defaultEnabled = getModelById(modelId)?.activation?.defaultActive !== false;
    if (enabled !== defaultEnabled) {
      normalized[modelId] = enabled;
    }
  }

  return normalized;
}

export function resolveModelActivation(overrides = {}) {
  return {
    ...getDefaultModelActivationState(),
    ...normalizeModelActivationOverrides(overrides),
  };
}

export function getNextModelActivationOverrides(overrides, modelId, enabled) {
  if (!getCurrentModelIdSet().has(modelId) || typeof enabled !== 'boolean') {
    return normalizeModelActivationOverrides(overrides);
  }

  const defaultEnabled = getModelById(modelId)?.activation?.defaultActive !== false;
  const next = normalizeModelActivationOverrides(overrides);

  if (enabled === defaultEnabled) {
    delete next[modelId];
  } else {
    next[modelId] = enabled;
  }

  return next;
}

export function isModelActive(modelOrId, activation = null) {
  const model = resolveModel(modelOrId);
  if (!model) return false;
  const resolvedActivation = activation || getDefaultModelActivationState();
  return resolvedActivation[model.id] !== false;
}

export function getModels({
  family = null,
  topology = null,
  surface = null,
  activeOnly = false,
  activation = null,
  search = '',
} = {}) {
  const activationState = activation || getDefaultModelActivationState();

  return getCurrentModelRepository().filter((model) => {
    if (family && model.family !== family) return false;
    if (!includesAll(model.topologies, topology)) return false;
    if (!includesAll(model.surfaces, surface)) return false;
    if (activeOnly && !isModelActive(model, activationState)) return false;
    return matchesSearch(model, search);
  });
}

export const getActiveModels = (filters = {}) => getModels({ ...filters, activeOnly: true });

export const getModelsByFamily = (family, filters = {}) => getModels({ ...filters, family });

export const getModelsByTopology = (topology, filters = {}) => getModels({ ...filters, topology });

export const getModelsBySurface = (surface, filters = {}) => getModels({ ...filters, surface });

export function getModelRouteContract(modelOrId, topology) {
  const model = resolveModel(modelOrId);
  if (!model || !topology) return null;
  return model.routes?.[topology] || null;
}

export function getModelRouteExecution(modelOrId, topology) {
  return getModelRouteContract(modelOrId, topology)?.execution || null;
}

export function getModelRouteAdapterId(modelOrId, topology) {
  return getModelRouteExecution(modelOrId, topology)?.adapterId || null;
}

export function getModelPresentation(modelOrId) {
  const model = resolveModel(modelOrId);
  return model?.presentation || null;
}

export function getModelControlTiers(modelOrId) {
  const tiers = resolveModel(modelOrId)?.ui?.controlTiers || null;
  return {
    core: uniqueStrings(tiers?.core),
    options: uniqueStrings(tiers?.options),
    hidden: uniqueStrings(tiers?.hidden),
  };
}

export function getModelTimingProfile(modelOrId) {
  return resolveModel(modelOrId)?.timing || null;
}

export function getRouteTimingProfile(modelOrId, topology = null) {
  const routeTiming = topology ? getModelRouteContract(modelOrId, topology)?.timing : null;
  return routeTiming || getModelTimingProfile(modelOrId);
}

export function getTimingProfileForModelKey(modelKey) {
  return getCurrentTimingProfileForKey(modelKey);
}

export function getPromptWorkflowKeys(modelOrId) {
  const prompt = resolveModel(modelOrId)?.prompt;
  if (!prompt) return [];
  return uniqueStrings(prompt.workflowKeys || []);
}

export function getAgentModelRoles(modelOrId) {
  return uniqueStrings(resolveModel(modelOrId)?.agent?.exposeAs || []);
}

export function getAgentDefaultRoles(modelOrId) {
  return uniqueStrings(resolveModel(modelOrId)?.agent?.defaultFor || []);
}

export function getFeatureContract(modelOrId, topology = null) {
  const model = resolveModel(modelOrId);
  if (!model) return {};
  const route = topology ? getModelRouteContract(model, topology) : null;
  return mergeFeatureContracts(model.features, route?.features);
}

export function getReferenceContract(modelOrId, topology = null) {
  const model = resolveModel(modelOrId);
  if (!model) return {};
  const route = topology ? getModelRouteContract(model, topology) : null;
  const routeReferences = route?.references || null;
  if (!routeReferences) return model.references;
  if (routeReferences.transport?.strategy === ReferenceTransportStrategy.NONE) {
    return routeReferences;
  }
  return mergeModelContracts(model.references, routeReferences);
}

export function getReferenceChipOptions(modelOrId, topology = null) {
  const contract = getReferenceContract(modelOrId, topology);
  return (contract.chips || []).map((chip) => ({
    value: chip,
    label: chip
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' '),
  }));
}

export function getSceneAssetReferenceKinds(modelOrId, topology = null) {
  const contract = getReferenceContract(modelOrId, topology);
  const kinds = uniqueStrings(contract.acceptedKinds)
    .map(getSceneAssetReferenceBucketKind)
    .filter(Boolean);
  return Array.from(new Set(kinds));
}

export function getSceneAssetReferenceLimit(modelOrId, kind, topology = null) {
  const normalizedKind = getSceneAssetReferenceBucketKind(kind);
  if (!normalizedKind) return 0;

  const contract = getReferenceContract(modelOrId, topology);
  const acceptedKinds = getSceneAssetReferenceKinds(modelOrId, topology);
  if (!acceptedKinds.includes(normalizedKind)) return 0;

  const bucketLimit = contract.transport?.buckets?.[normalizedKind]?.max;
  if (Number.isFinite(bucketLimit)) return Math.max(0, bucketLimit);

  const kindLimit = contract.limits?.[normalizedKind];
  if (Number.isFinite(kindLimit)) return Math.max(0, kindLimit);

  const imageLimit = contract.limits?.image;
  if (contract.acceptedMediaTypes?.includes('image') && Number.isFinite(imageLimit)) {
    return Math.max(0, imageLimit);
  }

  return 0;
}

export function getReferenceRoleValues(modelOrId, topology = null) {
  const contract = getReferenceContract(modelOrId, topology);
  return uniqueStrings(contract.roles);
}

export function getSupportedControls(modelOrId, topology = null) {
  const features = getFeatureContract(modelOrId, topology);
  return Object.entries(features)
    .filter(([, value]) => isFeatureEnabled(value))
    .map(([key]) => key);
}

export function getModelsForCapability(capability, filters = {}) {
  if (!capability || typeof capability !== 'string') return [];
  return getModels(filters).filter((model) => {
    if (filters.topology) {
      return getSupportedControls(model, filters.topology).includes(capability);
    }

    return model.topologies.some((topology) => getSupportedControls(model, topology).includes(capability));
  });
}

export function getModelsForQualityTier(qualityTier, filters = {}) {
  if (!qualityTier || typeof qualityTier !== 'string') return [];
  return getModels(filters).filter((model) => model.qualityTier === qualityTier);
}

/**
 * Raw registry query for models declaring an agent role. Runtime-facing agent
 * selection should use getAgentModelCatalog so availability, activation, and
 * confirmation gates are applied consistently.
 */
export function getRawModelsForAgentRole(role, filters = {}) {
  if (!role || typeof role !== 'string') return [];
  return getModels(filters).filter((model) => getAgentModelRoles(model).includes(role));
}

export function getModelOptions(filters = {}) {
  return getModels(filters).map((model) => {
    return {
      value: model.id,
      key: model.id,
      label: model.label,
      modelName: model.cost || model.provider,
      iconKey: model.iconKey,
      family: model.family,
      topologies: model.topologies,
      qualityTier: model.qualityTier || null,
    };
  });
}

export function getFamilyModelCounts({ activation = null } = {}) {
  const activationState = activation || getDefaultModelActivationState();

  return MODEL_FAMILY_ORDER.map((family) => {
    const models = getModels({ family });
    const activeModels = getModels({ family, activeOnly: true, activation: activationState });
    return {
      family,
      label: MODEL_FAMILY_LABELS[family],
      total: models.length,
      active: activeModels.length,
    };
  });
}
